import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Warning } from "@phosphor-icons/react";

// Every built-in Insurance column label — kept in sync with COLUMNS in InsurancePage.
const DEFAULT_LABELS = [
  "Insurance ID", "LU", "Attach.",
  "Insured Asset", "Insured Entity", "Type", "Category", "Type Code",
  "Expiry Date", "Broker", "Broker Phone", "Broker Email", "Carrier",
  "Financing", "Start / End", "Coverage Time", "Premium Price",
  "Policy Number", "Full Address",
];

function norm(s) {
  return (s || "").toString().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

export default function AddColumnModal({ open, onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");
  const [saving, setSaving] = useState(false);
  const [allCustom, setAllCustom] = useState([]);
  const [override, setOverride] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOverride(false);
    api.get("/insurance/columns", { params: { scope: "all" } })
      .then(({ data }) => setAllCustom(Array.isArray(data) ? data : []))
      .catch(() => setAllCustom([]));
  }, [open]);

  const duplicate = useMemo(() => {
    const target = norm(label);
    if (!target) return null;
    const hit = DEFAULT_LABELS.find((l) => norm(l) === target);
    if (hit) return { label: hit, kind: "default" };
    const cc = allCustom.find((c) => norm(c.label) === target);
    if (cc) return { label: cc.label, kind: cc.deleted ? "deleted" : "custom" };
    return null;
  }, [label, allCustom]);

  const reset = () => { setLabel(""); setType("text"); setOverride(false); };

  const save = async () => {
    const l = label.trim();
    if (!l) { toast.error("Column name is required"); return; }
    if (duplicate && !override) { setOverride(true); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/insurance/columns", { label: l, type });
      toast.success(`Column "${data.label}" added`);
      reset();
      onCreated && onCreated(data);
      onClose();
    } catch (e) {
      const m = apiErrorMessage(e); if (m) toast.error(m);
    } finally { setSaving(false); }
  };

  const dupCopy = duplicate && (
    duplicate.kind === "default"
      ? `"${duplicate.label}" is already a built-in column.`
      : duplicate.kind === "deleted"
        ? `A previously-deleted custom column was named "${duplicate.label}" — you may want to restore it instead of creating a duplicate.`
        : `A custom column named "${duplicate.label}" already exists.`
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-md" data-testid="insurance-add-column-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus size={16} className="text-[#327de3]" /> Add a column
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-[11px]">
            <span className="uppercase tracking-widest font-semibold text-slate-500">Column name</span>
            <Input
              value={label}
              onChange={(e) => { setLabel(e.target.value); setOverride(false); }}
              placeholder="e.g. Deductible, Sub-limit"
              autoFocus
              maxLength={48}
              className="h-9 text-sm mt-1"
              data-testid="insurance-add-column-label"
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            />
          </label>
          <label className="block text-[11px]">
            <span className="uppercase tracking-widest font-semibold text-slate-500">Data type</span>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="h-9 w-full border border-slate-300 rounded-md px-2 bg-white text-sm mt-1"
              data-testid="insurance-add-column-type"
            >
              <option value="text">Text</option>
              <option value="number">Number</option>
              <option value="money">Money ($)</option>
              <option value="percent">Percent (%)</option>
              <option value="date">Date</option>
            </select>
          </label>
          {duplicate && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 flex items-start gap-2" data-testid="insurance-add-column-warning">
              <Warning size={14} className="text-amber-600 shrink-0 mt-0.5" />
              <div className="text-[11px] text-amber-900">
                <div className="font-bold">Similar column detected</div>
                <div>{dupCopy} {override ? " — click Add anyway to proceed." : ""}</div>
              </div>
            </div>
          )}
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600">
            New columns append to the far right by default. Drag any header to reorder — your choice is remembered per device.
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={saving}>Cancel</Button>
          <Button
            onClick={save}
            disabled={saving}
            className={duplicate && override ? "bg-amber-500 hover:bg-amber-600" : "bg-[#327de3] hover:bg-[#1f64c2]"}
            data-testid="insurance-add-column-save"
          >
            {saving ? "Adding…" : duplicate ? (override ? "Add anyway" : "Add") : "Add column"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
